import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { meetingApi } from "@/api"
import type { MeetingProfile } from "@/api/types"
import { logger } from "@/lib/logger"
import {
  selectMeetingProfile,
  selectMeetingProfileLastUpdated,
  setMeetingProfile,
  store,
  useAppSelector,
} from "@/store"

const STALE_THRESHOLD_MS = 10_000
const inflightMeetingProfileRequests = new Map<
  string,
  Promise<MeetingProfile | null>
>()

interface UseMeetingProfileOptions {
  roomId?: string
  staleTime?: number
  onError?: (error: Error) => void
  autoFetch?: boolean
}

interface UseMeetingProfileReturn {
  meetingProfile: MeetingProfile | null
  isLoading: boolean
  refetch: () => Promise<MeetingProfile | null>
  lastUpdated: number | null
}

export const fetchMeetingProfile = async ({
  roomId,
}: {
  roomId: string
}): Promise<MeetingProfile> => {
  return meetingApi.getMeetingProfile({ roomId })
}

const loadMeetingProfile = async ({
  roomId,
  force = false,
  staleTime = STALE_THRESHOLD_MS,
}: {
  roomId?: string
  force?: boolean
  staleTime?: number
}): Promise<MeetingProfile | null> => {
  if (!roomId) return null

  const state = store.getState()
  const lastUpdated = selectMeetingProfileLastUpdated(roomId)(state)
  const isStale = force || !lastUpdated || Date.now() - lastUpdated > staleTime

  if (!isStale) {
    return selectMeetingProfile(roomId)(state)
  }

  const inflightRequest = inflightMeetingProfileRequests.get(roomId)
  if (inflightRequest) {
    return inflightRequest
  }

  const request = fetchMeetingProfile({ roomId })
    .then((meetingProfile) => {
      store.dispatch(setMeetingProfile(meetingProfile))
      return meetingProfile
    })
    .finally(() => {
      inflightMeetingProfileRequests.delete(roomId)
    })

  inflightMeetingProfileRequests.set(roomId, request)
  return request
}

export const preloadMeetingProfile = async ({
  roomId,
  force = false,
  staleTime = STALE_THRESHOLD_MS,
}: {
  roomId?: string
  force?: boolean
  staleTime?: number
}): Promise<MeetingProfile | null> => {
  return loadMeetingProfile({ roomId, force, staleTime })
}

export const useMeetingProfile = ({
  roomId,
  staleTime = STALE_THRESHOLD_MS,
  onError,
  autoFetch = true,
}: UseMeetingProfileOptions = {}): UseMeetingProfileReturn => {
  const meetingProfileSelector = useMemo(
    () => (roomId ? selectMeetingProfile(roomId) : () => null),
    [roomId],
  )
  const lastUpdatedSelector = useMemo(
    () => (roomId ? selectMeetingProfileLastUpdated(roomId) : () => null),
    [roomId],
  )
  const meetingProfile = useAppSelector(meetingProfileSelector)
  const lastUpdated = useAppSelector(lastUpdatedSelector)
  const currentRoomIdRef = useRef(roomId)
  currentRoomIdRef.current = roomId
  const loadingRequestRef = useRef<Promise<MeetingProfile | null> | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchProfile = useCallback(async (): Promise<MeetingProfile | null> => {
    if (!roomId) return null
    setIsLoading(true)
    const request = loadMeetingProfile({
      roomId,
      force: true,
      staleTime,
    })
    loadingRequestRef.current = request
    try {
      return await request
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("Failed to fetch meeting profile")
      logger.error("Failed to fetch meeting profile", err)
      if (currentRoomIdRef.current === roomId) {
        onError?.(error)
      }
      return null
    } finally {
      if (loadingRequestRef.current === request) {
        loadingRequestRef.current = null
        setIsLoading(false)
      }
    }
  }, [onError, roomId, staleTime])

  useEffect(() => {
    if (!roomId) return

    const isStale = !lastUpdated || Date.now() - lastUpdated > staleTime
    if (isStale && autoFetch) {
      void fetchProfile()
    }
  }, [fetchProfile, lastUpdated, roomId, staleTime, autoFetch])

  return {
    meetingProfile,
    isLoading,
    refetch: fetchProfile,
    lastUpdated,
  }
}
